import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
  Sparkles, Plus, Upload, FileText, Plug, Bot, Smartphone, Monitor,
  Loader2, Copy, Trash2, Settings2, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { FarmTypeFields } from '@/components/FarmTypeFields';

const CATEGORY_LABELS = { reddit: 'Reddit', linkedin: 'LinkedIn', x: 'X', generic: 'General', marketing: 'Marketing' };

function apiError(e, fallback = 'Request failed') {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string' && d.trim()) return d;
  if (d && typeof d === 'object') return d.message || d.code || JSON.stringify(d);
  return e?.message || fallback;
}

export default function Templates() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [editSlug, setEditSlug] = React.useState(null);
  const [filter, setFilter] = React.useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get('/templates').then((r) => r.data),
  });

  const items = React.useMemo(() => {
    let list = data?.items || [];
    if (filter === 'org') list = list.filter((t) => !t.is_global);
    if (filter === 'global') list = list.filter((t) => t.is_global);
    if (filter === 'browser') list = list.filter((t) => (t.runtime || 'browser') === 'browser');
    if (filter === 'mobile') list = list.filter((t) => t.runtime === 'mobile');
    return list;
  }, [data, filter]);

  const cloneMut = useMutation({
    mutationFn: (slug) => api.post(`/templates/${slug}/clone`).then((r) => r.data),
    onSuccess: (t) => {
      toast.success('Template cloned — you can edit playbook/skill/MCP now');
      qc.invalidateQueries({ queryKey: ['templates'] });
      setEditSlug(t.slug);
    },
    onError: (e) => toast.error(apiError(e, 'Clone failed')),
  });

  const deleteMut = useMutation({
    mutationFn: (slug) => api.delete(`/templates/${slug}`),
    onSuccess: () => {
      toast.success('Template deleted');
      qc.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (e) => toast.error(apiError(e, 'Delete failed')),
  });

  return (
    <div className="space-y-6" data-testid="templates-page">
      <PageHeader
        eyebrow="Library"
        title="Agent Templates"
        description="Upload playbooks + skill.md, configure variables, connect MCP, then spin up agents."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setUploadOpen(true)} data-testid="templates-upload-button">
              <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload playbook
            </Button>
            <Button size="sm" onClick={() => setUploadOpen(true)} data-testid="templates-new-button">
              <Plus className="mr-1.5 h-3.5 w-3.5" /> New template
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-2">
        {[
          ['all', 'All'],
          ['global', 'Built-in'],
          ['org', 'My templates'],
          ['browser', 'Browser farm'],
          ['mobile', 'Mobile farm'],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
              filter === k
                ? 'border-[hsl(var(--primary))/0.4] bg-[hsl(var(--primary))/0.12] text-[hsl(var(--primary))]'
                : 'border-white/10 text-[hsl(var(--text-3))] hover:border-white/20'
            }`}
            data-testid={`templates-filter-${k}`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skel h-52 rounded-lg" data-testid="loading-skeleton" />)}
        </div>
      ) : !items.length ? (
        <EmptyState
          icon={Sparkles}
          title="No templates yet"
          description="Upload a playbook.md / skill.md (agency-agents style) to create a configurable agent template."
          action={<Button size="sm" onClick={() => setUploadOpen(true)}>Upload playbook</Button>}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((t) => (
            <div
              key={t.id}
              className="group flex flex-col rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] p-5 hover:border-white/15 transition-colors"
              data-testid={`template-card-${t.slug}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded-full border border-[hsl(var(--primary))/0.3] bg-[hsl(var(--primary))/0.1] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[hsl(var(--primary))]">
                    {CATEGORY_LABELS[t.category] || t.category}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[hsl(var(--text-3))]">
                    {(t.runtime || 'browser') === 'mobile'
                      ? <><Smartphone className="h-3 w-3" /> Mobile</>
                      : <><Monitor className="h-3 w-3" /> Browser</>}
                  </span>
                  {!t.is_global && (
                    <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[hsl(var(--text-3))]">Custom</span>
                  )}
                </div>
                <Sparkles className="h-4 w-4 text-[hsl(var(--text-3))] group-hover:text-[hsl(var(--primary))]" />
              </div>

              <h3 className="mt-3 text-base font-semibold tracking-tight">{t.name}</h3>
              <p className="mt-1 text-sm text-[hsl(var(--text-3))] line-clamp-3 flex-1">{t.description}</p>

              <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-[hsl(var(--text-3))]">
                {t.playbook_md ? (
                  <span className="inline-flex items-center gap-1 rounded border border-white/10 px-1.5 py-0.5"><FileText className="h-3 w-3" /> Playbook</span>
                ) : null}
                {t.skill_md ? (
                  <span className="inline-flex items-center gap-1 rounded border border-white/10 px-1.5 py-0.5"><Bot className="h-3 w-3" /> Skill</span>
                ) : null}
                {(t.mcp_servers || []).length > 0 ? (
                  <span className="inline-flex items-center gap-1 rounded border border-white/10 px-1.5 py-0.5"><Plug className="h-3 w-3" /> {(t.mcp_servers || []).length} MCP</span>
                ) : null}
                {(t.variables || []).length > 0 ? (
                  <span className="inline-flex items-center gap-1 rounded border border-white/10 px-1.5 py-0.5"><Settings2 className="h-3 w-3" /> {(t.variables || []).length} vars</span>
                ) : null}
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => nav(`/agents?new=1&template=${t.slug}`)}
                  data-testid={`template-use-${t.slug}`}
                >
                  Use template
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditSlug(t.slug)}
                  data-testid={`template-configure-${t.slug}`}
                >
                  Configure
                </Button>
                {t.is_global ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => cloneMut.mutate(t.slug)}
                    disabled={cloneMut.isPending}
                    title="Clone to editable org template"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteMut.mutate(t.slug)}
                    disabled={deleteMut.isPending}
                    title="Delete template"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--danger))]" />
                  </Button>
                )}
              </div>
              <div className="mt-2 font-mono text-[10px] text-[hsl(var(--text-3))]">{t.slug}</div>
            </div>
          ))}
        </div>
      )}

      <UploadTemplateSheet open={uploadOpen} onClose={() => setUploadOpen(false)} onCreated={(t) => { setUploadOpen(false); setEditSlug(t.slug); }} />
      {editSlug && (
        <ConfigureTemplateSheet
          slug={editSlug}
          onClose={() => setEditSlug(null)}
          onCloned={(slug) => setEditSlug(slug)}
          onUse={(slug) => { setEditSlug(null); nav(`/agents?new=1&template=${slug}`); }}
        />
      )}
    </div>
  );
}

function UploadTemplateSheet({ open, onClose, onCreated }) {
  const qc = useQueryClient();
  const [form, setForm] = React.useState({
    name: '', description: '', category: 'reddit', runtime: 'browser', mission: '',
  });
  const [playbookFile, setPlaybookFile] = React.useState(null);
  const [skillFile, setSkillFile] = React.useState(null);
  const [playbookText, setPlaybookText] = React.useState('');
  const [skillText, setSkillText] = React.useState('');

  const mut = useMutation({
    mutationFn: async () => {
      if (playbookFile || skillFile) {
        const fd = new FormData();
        fd.append('name', form.name || playbookFile?.name?.replace(/\.md$/i, '') || 'Imported Agent');
        fd.append('description', form.description || '');
        fd.append('category', form.category);
        fd.append('runtime', form.runtime);
        fd.append('mission', form.mission || '');
        if (playbookFile) fd.append('playbook', playbookFile);
        if (skillFile) fd.append('skill', skillFile);
        const r = await api.post('/templates/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        return r.data;
      }
      const r = await api.post('/templates', {
        name: form.name,
        description: form.description,
        category: form.category,
        runtime: form.runtime,
        mission: form.mission || `Follow the ${form.name} playbook.`,
        playbook_md: playbookText,
        skill_md: skillText,
        variables: [
          { key: 'promote_website', label: 'Website / offer URL', type: 'string', required: false },
          { key: 'promote_keywords', label: 'Keywords', type: 'string_list', required: false },
          { key: 'farm_niches', label: 'Niches / subreddits', type: 'string_list', required: false },
          { key: 'user_notes', label: 'Operator notes', type: 'text', required: false },
        ],
        mcp_servers: [],
        config: {},
      });
      return r.data;
    },
    onSuccess: (t) => {
      toast.success('Template created');
      qc.invalidateQueries({ queryKey: ['templates'] });
      onCreated?.(t);
    },
    onError: (e) => toast.error(apiError(e, 'Upload failed')),
  });

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-[640px] flex flex-col" data-testid="upload-template-sheet">
        <SheetHeader>
          <SheetTitle>Upload agentic template</SheetTitle>
          <SheetDescription>
            Drop a playbook.md and/or skill.md (agency-agents style). Then configure variables and MCP servers.
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          <div className="rounded-md border border-white/10 bg-[hsl(var(--surface-2))] p-3 text-xs text-[hsl(var(--text-2))]">
            Example format:{' '}
            <a
              className="inline-flex items-center gap-1 text-[hsl(var(--primary))] hover:underline"
              href="https://github.com/msitarzewski/agency-agents/blob/main/marketing/marketing-reddit-community-builder.md"
              target="_blank"
              rel="noreferrer"
            >
              marketing-reddit-community-builder.md <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Reddit Community Builder" data-testid="template-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="reddit">Reddit</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="x">X</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="generic">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <FarmTypeFields
            runtime={form.runtime}
            onRuntimeChange={(v) => setForm({ ...form, runtime: v })}
            showProfile={false}
            runtimeTestId="template-farm-type-select"
          />

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short summary of what this agent does" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Playbook.md file</Label>
              <Input type="file" accept=".md,text/markdown,text/plain" onChange={(e) => setPlaybookFile(e.target.files?.[0] || null)} data-testid="template-playbook-file" />
            </div>
            <div className="space-y-1.5">
              <Label>Skill.md file</Label>
              <Input type="file" accept=".md,text/markdown,text/plain" onChange={(e) => setSkillFile(e.target.files?.[0] || null)} data-testid="template-skill-file" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Or paste playbook markdown</Label>
            <Textarea rows={6} value={playbookText} onChange={(e) => setPlaybookText(e.target.value)} placeholder="---&#10;name: Reddit Community Builder&#10;---&#10;&#10;## Core Mission&#10;..." data-testid="template-playbook-text" />
          </div>
          <div className="space-y-1.5">
            <Label>Or paste skill.md</Label>
            <Textarea rows={4} value={skillText} onChange={(e) => setSkillText(e.target.value)} placeholder="Identity, communication style, capabilities..." data-testid="template-skill-text" />
          </div>
        </div>
        <SheetFooter className="border-t border-white/5 pt-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || (!form.name && !playbookFile && !playbookText)} data-testid="template-upload-submit">
            {mut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create template
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function ConfigureTemplateSheet({ slug, onClose, onUse, onCloned }) {
  const qc = useQueryClient();
  const { data: t, isLoading } = useQuery({
    queryKey: ['template', slug],
    queryFn: () => api.get(`/templates/${slug}`).then((r) => r.data),
  });

  const [form, setForm] = React.useState(null);
  React.useEffect(() => {
    if (t) {
      setForm({
        name: t.name,
        description: t.description,
        mission: t.mission,
        category: t.category,
        runtime: t.runtime || 'browser',
        playbook_md: t.playbook_md || '',
        skill_md: t.skill_md || '',
        mcp_servers: (t.mcp_servers || []).map((m) => ({
          id: m.id || '',
          transport: m.transport || 'sse',
          url: m.url || '',
          command: m.command || '',
          tools: Array.isArray(m.tools) ? m.tools.join(', ') : '*',
          enabled: m.enabled !== false,
        })),
        variables: (t.variables || []).map((v) => ({
          key: v.key || '',
          label: v.label || '',
          type: v.type || 'string',
          required: !!v.required,
          placeholder: v.placeholder || '',
          help: v.help || '',
        })),
      });
    }
  }, [t]);

  const saveMut = useMutation({
    mutationFn: (body) => api.patch(`/templates/${slug}`, body).then((r) => r.data),
    onSuccess: () => {
      toast.success('Template saved');
      qc.invalidateQueries({ queryKey: ['templates'] });
      qc.invalidateQueries({ queryKey: ['template', slug] });
    },
    onError: (e) => toast.error(apiError(e, 'Save failed')),
  });

  const cloneMut = useMutation({
    mutationFn: () => api.post(`/templates/${slug}/clone`).then((r) => r.data),
    onSuccess: (nt) => {
      toast.success('Cloned — editing your copy');
      qc.invalidateQueries({ queryKey: ['templates'] });
      onCloned?.(nt.slug);
    },
    onError: (e) => toast.error(apiError(e, 'Clone failed')),
  });

  const readOnly = !!t?.is_global;

  const save = () => {
    if (!form) return;
    if (readOnly) {
      toast.error('Built-in templates are read-only. Clone first to edit.');
      return;
    }
    saveMut.mutate({
      name: form.name,
      description: form.description,
      mission: form.mission,
      category: form.category,
      runtime: form.runtime,
      playbook_md: form.playbook_md,
      skill_md: form.skill_md,
      variables: form.variables.filter((v) => v.key.trim()).map((v) => ({
        key: v.key.trim(),
        label: v.label || v.key,
        type: v.type,
        required: !!v.required,
        placeholder: v.placeholder || '',
        help: v.help || '',
      })),
      mcp_servers: form.mcp_servers.filter((m) => m.id.trim()).map((m) => ({
        id: m.id.trim(),
        transport: m.transport || 'sse',
        url: m.url || '',
        command: m.command || '',
        tools: String(m.tools || '*').split(',').map((x) => x.trim()).filter(Boolean),
        enabled: m.enabled !== false,
      })),
    });
  };

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-[720px] flex flex-col" data-testid="configure-template-sheet">
        <SheetHeader>
          <SheetTitle>Configure template</SheetTitle>
          <SheetDescription>
            {readOnly
              ? 'This is a built-in template. Clone it to edit playbook, skill, variables, and MCP.'
              : 'Edit playbook, skill.md, configurable variables, and MCP servers.'}
          </SheetDescription>
        </SheetHeader>

        {isLoading || !form ? (
          <div className="py-10 text-center text-sm text-[hsl(var(--text-3))]">Loading…</div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-5 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input disabled={readOnly} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select disabled={readOnly} value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reddit">Reddit</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="x">X</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="generic">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <FarmTypeFields
              runtime={form.runtime}
              onRuntimeChange={(v) => setForm({ ...form, runtime: v })}
              disabled={readOnly}
              showProfile={false}
              runtimeTestId="configure-farm-type-select"
            />

            <div className="space-y-1.5">
              <Label>Mission summary</Label>
              <Textarea disabled={readOnly} rows={3} value={form.mission} onChange={(e) => setForm({ ...form, mission: e.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label>Playbook.md</Label>
              <Textarea disabled={readOnly} rows={8} className="font-mono text-xs" value={form.playbook_md} onChange={(e) => setForm({ ...form, playbook_md: e.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label>Skill.md</Label>
              <Textarea disabled={readOnly} rows={6} className="font-mono text-xs" value={form.skill_md} onChange={(e) => setForm({ ...form, skill_md: e.target.value })} />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Configurable variables</Label>
                {!readOnly && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setForm({
                      ...form,
                      variables: [...form.variables, { key: '', label: '', type: 'string', required: false, placeholder: '', help: '' }],
                    })}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add variable
                  </Button>
                )}
              </div>
              {form.variables.length === 0 && (
                <p className="text-xs text-[hsl(var(--text-3))]">No variables — agents created from this template won’t prompt for extra config.</p>
              )}
              {form.variables.map((v, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 rounded-md border border-white/5 p-2">
                  <Input disabled={readOnly} className="col-span-3" placeholder="key" value={v.key} onChange={(e) => {
                    const next = [...form.variables]; next[idx] = { ...v, key: e.target.value }; setForm({ ...form, variables: next });
                  }} />
                  <Input disabled={readOnly} className="col-span-3" placeholder="Label" value={v.label} onChange={(e) => {
                    const next = [...form.variables]; next[idx] = { ...v, label: e.target.value }; setForm({ ...form, variables: next });
                  }} />
                  <Select disabled={readOnly} value={v.type} onValueChange={(val) => {
                    const next = [...form.variables]; next[idx] = { ...v, type: val }; setForm({ ...form, variables: next });
                  }}>
                    <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="string">String</SelectItem>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="string_list">List</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="boolean">Boolean</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="col-span-2 flex items-center gap-2 text-xs">
                    <Switch disabled={readOnly} checked={!!v.required} onCheckedChange={(c) => {
                      const next = [...form.variables]; next[idx] = { ...v, required: c }; setForm({ ...form, variables: next });
                    }} />
                    Req
                  </div>
                  {!readOnly && (
                    <Button type="button" variant="ghost" size="sm" className="col-span-1" onClick={() => {
                      setForm({ ...form, variables: form.variables.filter((_, i) => i !== idx) });
                    }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>MCP servers</Label>
                {!readOnly && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setForm({
                      ...form,
                      mcp_servers: [...form.mcp_servers, { id: '', transport: 'sse', url: '', command: '', tools: '*', enabled: true }],
                    })}
                  >
                    <Plug className="mr-1 h-3.5 w-3.5" /> Add MCP
                  </Button>
                )}
              </div>
              <p className="text-xs text-[hsl(var(--text-3))]">
                Connect MCP so the agent can call external tools when the playbook requires it (search, CRM, docs, etc.).
              </p>
              {form.mcp_servers.map((m, idx) => (
                <div key={idx} className="space-y-2 rounded-md border border-white/5 p-3">
                  <div className="grid grid-cols-12 gap-2">
                    <Input disabled={readOnly} className="col-span-3" placeholder="id (github)" value={m.id} onChange={(e) => {
                      const next = [...form.mcp_servers]; next[idx] = { ...m, id: e.target.value }; setForm({ ...form, mcp_servers: next });
                    }} />
                    <Select disabled={readOnly} value={m.transport} onValueChange={(val) => {
                      const next = [...form.mcp_servers]; next[idx] = { ...m, transport: val }; setForm({ ...form, mcp_servers: next });
                    }}>
                      <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sse">SSE</SelectItem>
                        <SelectItem value="http">HTTP</SelectItem>
                        <SelectItem value="stdio">stdio</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input disabled={readOnly} className="col-span-5" placeholder="URL or command" value={m.url || m.command} onChange={(e) => {
                      const next = [...form.mcp_servers];
                      next[idx] = m.transport === 'stdio'
                        ? { ...m, command: e.target.value }
                        : { ...m, url: e.target.value };
                      setForm({ ...form, mcp_servers: next });
                    }} />
                    <div className="col-span-1 flex items-center justify-center">
                      <Switch disabled={readOnly} checked={m.enabled !== false} onCheckedChange={(c) => {
                        const next = [...form.mcp_servers]; next[idx] = { ...m, enabled: c }; setForm({ ...form, mcp_servers: next });
                      }} />
                    </div>
                  </div>
                  <Input disabled={readOnly} placeholder="Allowed tools (* or comma-separated)" value={m.tools} onChange={(e) => {
                    const next = [...form.mcp_servers]; next[idx] = { ...m, tools: e.target.value }; setForm({ ...form, mcp_servers: next });
                  }} />
                  {!readOnly && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, mcp_servers: form.mcp_servers.filter((_, i) => i !== idx) })}>
                      <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove MCP
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <SheetFooter className="border-t border-white/5 pt-4 gap-2">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {readOnly ? (
            <Button variant="secondary" onClick={() => cloneMut.mutate()} disabled={cloneMut.isPending}>
              {cloneMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Clone to edit
            </Button>
          ) : (
            <Button variant="secondary" onClick={save} disabled={saveMut.isPending}>
              {saveMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          )}
          <Button onClick={() => onUse(slug)}>Use template</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
